import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { Solution } from '../models/solution.model';

export interface SolutionFilter {
  industries: string[];
  disciplines: string[];
  materials: string[];
}

@Injectable({
  providedIn: 'root'
})
export class SolutionsService {
  private readonly dataUrl = 'assets/data/solutions.json';

  constructor(private readonly http: HttpClient) {}

  getSolutions(): Observable<Solution[]> {
    return this.http.get<Solution[]>(this.dataUrl);
  }

  getMatchingSolutionIds(filter: SolutionFilter): Observable<number[]> {
    const industries = filter.industries ?? [];
    const disciplines = filter.disciplines ?? [];
    const materials = filter.materials ?? [];
    const hasFilters = industries.length > 0 ||
      disciplines.length > 0 ||
      materials.length > 0;

    if (!hasFilters) {
      return of([]);
    }

    return this.getSolutions().pipe(
      map(solutions => this.filterSolutions(solutions, filter).map(s => s.id))
    );
  }

  getFilteredSolutions(filter: SolutionFilter): Observable<Solution[]> {
    return this.getSolutions().pipe(
      map(solutions => this.filterSolutions(solutions, filter))
    );
  }

  private filterSolutions(solutions: Solution[], filter: SolutionFilter): Solution[] {
    const industries = filter.industries ?? [];
    const disciplines = filter.disciplines ?? [];
    const materials = filter.materials ?? [];

    return solutions.filter(solution => {
      // If no filters selected, show all solutions
      const hasFilters = industries.length > 0 || 
             disciplines.length > 0 || 
             materials.length > 0;
      
      if (!hasFilters) {
        return true;
      }

      // BIMPLUS: materials = null means always passes material filter
      const solutionMaterials = solution.materials;

      if (solution.filterMode === 'AND') {
        // Prefab (MANUFACTON): ALL specified conditions must match exactly
        const industryMatch = industries.length === 0 ||
          (solution.industries.length > 0 && solution.industries.every(i => industries.includes(i)) &&
           industries.every(i => solution.industries.includes(i)));
        const disciplineMatch = disciplines.length === 0 ||
          disciplines.some(d => solution.disciplines.includes(d));
        const materialMatch = materials.length === 0 || solutionMaterials === null ||
          materials.every(m => solutionMaterials.includes(m)) && solutionMaterials.every(m => materials.includes(m));
        return industryMatch && disciplineMatch && materialMatch;
      }

      // Standard OR logic: any match per category is sufficient
      const industryMatch = industries.length === 0 || 
        industries.some(i => solution.industries.includes(i));
      const disciplineMatch = disciplines.length === 0 || 
        disciplines.some(d => solution.disciplines.includes(d));
      const materialMatch = materials.length === 0 || solutionMaterials === null ||
        materials.some(m => solutionMaterials.includes(m));

      return industryMatch && disciplineMatch && materialMatch;
    });
  }

  /** Returns the suggested edition IDs based on the currently highlighted (matched) solutions. */
  getSuggestedEditionIds(highlightedSolutions: Solution[]): string[] {
    const ids = new Set(highlightedSolutions.map(s => s.id));
    const suggested: string[] = [];

    if (ids.has(1) && ids.has(2)) {
      suggested.push('civil');
    } else if (ids.has(1)) {
      suggested.push('allplan-ultimate');
    }
    if (ids.has(4)) {
      suggested.push('scia-pro');
    }
    if (ids.has(3)) {
      suggested.push('frilo-ultimate');
    }
    if (ids.has(5)) {
      suggested.push('sds2-ultimate');
    }
    if (ids.has(7)) {
      suggested.push('manufacton-pro');
    }
    if (ids.has(6) && !suggested.includes('allplan-ultimate') && !suggested.includes('civil')) {
      suggested.push('allplan-ultimate');
    }

    return suggested;
  }
}
